import React, { useState } from 'react';
import { ChevronDown, ChevronUp, PlayCircle } from 'lucide-react';

export default function Module04({ data }) {
  const { day, split, warmup, exercises = [], cooldown, duration_min } = data;

  return (
    <div className="space-y-5">

      {/* Session header */}
      <div className="border-b border-white/8 pb-5">
        <p className="text-[9px] tracking-[0.3em] uppercase mb-2" style={{ color: 'var(--gold)' }}>{day}</p>
        <p className="text-lg font-light tracking-[0.08em] uppercase">{split}</p>
        <div className="flex gap-4 mt-2">
          <span className="text-[10px] tracking-[0.15em] uppercase text-gray-600">{duration_min} min</span>
          <span className="text-[10px] tracking-[0.15em] uppercase text-gray-600">{exercises.length} exercises</span>
        </div>
      </div>

      {/* Warm-up */}
      {warmup && <Section title="Warm-Up"><p className="text-sm text-gray-400 leading-relaxed">{warmup}</p></Section>}

      {/* Exercises */}
      <Section title="Main Workout" defaultOpen>
        <div>
          {exercises.map((ex, i) => <ExerciseRow key={i} index={i + 1} exercise={ex} />)}
        </div>
      </Section>

      {/* Cool-down */}
      {cooldown && <Section title="Cool-Down"><p className="text-sm text-gray-400 leading-relaxed">{cooldown}</p></Section>}
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/8">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 active:bg-white/3">
        <span className="text-[9px] tracking-[0.3em] uppercase text-gray-400">{title}</span>
        {open
          ? <ChevronUp size={14} className="text-gray-600" />
          : <ChevronDown size={14} className="text-gray-600" />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-white/8">{children}</div>}
    </div>
  );
}

function ExerciseRow({ index, exercise }) {
  const [showCues, setShowCues] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  return (
    <div className="py-3.5 border-b border-white/5 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="flex gap-4 flex-1">
          <span className="text-[10px] text-gray-700 w-4 shrink-0 mt-0.5">{index}</span>
          <div className="flex-1">
            <p className="text-sm font-medium tracking-wide">{exercise.name}</p>
            <p className="text-[10px] tracking-[0.1em] uppercase mt-1" style={{ color: 'var(--gold)' }}>
              {exercise.sets} sets × {exercise.reps} · Rest {exercise.rest}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {exercise.video_id && (
            <button onClick={() => setShowDemo(!showDemo)}
              className="flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase text-gray-600 hover:text-white transition-colors">
              <PlayCircle size={12} style={showDemo ? { color: 'var(--gold)' } : {}} />
              {showDemo ? 'hide' : 'demo'}
            </button>
          )}
          {exercise.cues && (
            <button onClick={() => setShowCues(!showCues)}
              className="text-[9px] tracking-[0.15em] uppercase text-gray-600 hover:text-white transition-colors">
              {showCues ? 'hide' : 'cues'}
            </button>
          )}
        </div>
      </div>

      {showDemo && exercise.video_id && (
        <div className="mt-3 ml-8 overflow-hidden aspect-video border border-white/10">
          <iframe
            src={`https://www.youtube.com/embed/${exercise.video_id}?rel=0&modestbranding=1`}
            title={`${exercise.name} demo`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}

      {showCues && exercise.cues && (
        <p className="text-[11px] text-gray-500 mt-2 ml-8 leading-relaxed">{exercise.cues}</p>
      )}
    </div>
  );
}
